import { useEffect, useRef } from 'react'
import cn from 'classnames'
import { Button, BUTTON_TYPES } from '../Button'
import { Popup } from '../Popup'
import usePopup from '../../hooks/usePopup'
import { useShowPopup } from '../../hooks/useShowPopup'
import { BTN_TXT } from '../../constants'
import { FloatFooterProps } from './types'
import './styles.scss'

export const FloatFooter = ({
  className,
  onCancel,
  isActive,
  onSave,
  withConfirm,
  htmlType = 'button',
  saveBtnText = BTN_TXT.SAVE,
  isSaveDisabled = false,
  isSingleBtn
}: FloatFooterProps) => {
  const [showPopup, confirmNavigation, cancelNavigation] =
    useShowPopup(isActive)
  const discardChangesPopup = usePopup()
  const confirmChangesPopup = usePopup()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    if (isActive) {
      containerRef.current = document.querySelector('.container')
      if (containerRef.current) {
        resizeObserverRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.contentBoxSize && containerRef.current) {
              if (
                containerRef.current.scrollHeight >
                containerRef.current.clientHeight - 48
              ) {
                containerRef.current.classList.add('container--active')
              } else {
                containerRef.current.classList.remove('container--active')
              }
            }
          }
        })
        resizeObserverRef.current.observe(containerRef.current)
      }
    }
  }, [isActive])

  useEffect(() => {
    return () => resizeObserverRef.current?.disconnect()
  }, [])

  const resetFields = () => {
    if (isActive) {
      discardChangesPopup.actions.open()
    } else {
      submitCancellation()
    }
  }

  const submitCancellation = () => {
    discardChangesPopup.actions.close()
    onCancel()
  }

  const onSaveChanges = () => {
    if (isActive && withConfirm) {
      confirmChangesPopup.actions.open()
    } else if (!withConfirm && onSave) {
      onSave()
    }
  }

  const onSaveAction = async () => {
    if (onSave) {
      onSave()
    }
    confirmChangesPopup.actions.close()
  }

  const cancelDiscardChanges = () => discardChangesPopup.actions.close()

  return (
    <>
      {(isActive || isSingleBtn) && (
        <div className={cn('float-footer row justify-end gap-8', className)}>
          {!isSingleBtn && (
            <Button onClick={resetFields} upperCase>
              {BTN_TXT.CANCEL}
            </Button>
          )}
          <Button
            type={BUTTON_TYPES.PRIMARY}
            onClick={onSaveChanges}
            htmlType={htmlType}
            disabled={isSaveDisabled}
            upperCase
          >
            {!!saveBtnText ? saveBtnText : BTN_TXT.SAVE}
          </Button>
        </div>
      )}
      <Popup.Discard
        visible={showPopup ? showPopup : discardChangesPopup.state.visible}
        onCancel={showPopup ? cancelNavigation : cancelDiscardChanges}
        onSubmit={showPopup ? confirmNavigation : submitCancellation}
      />
      {withConfirm && (
        <Popup.ConfirmChanges
          visible={confirmChangesPopup.state.visible}
          onCancel={confirmChangesPopup.actions.close}
          onSubmit={onSaveAction}
        />
      )}
    </>
  )
}
